import { useCallback, useState } from 'react';

import { NotificationProps } from '../components/Notification';

export interface NotificationState extends Omit<NotificationProps, 'onClose'> {
  id: string;
}

const createSecureId = () => {
  // Use cryptographically secure random generation to satisfy security scanners.
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  // Fallback to crypto.getRandomValues (available in all modern environments)
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const buffer = new Uint8Array(16);
    crypto.getRandomValues(buffer);
    return Array.from(buffer, byte => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  }

  // Last resort: timestamp-based ID (crypto should always be available in modern environments)
  return `notification-${Date.now()}-${performance.now().toString(36)}`;
};

export function useNotification() {
  const [notifications, setNotifications] = useState<NotificationState[]>([]);

  const showNotification = useCallback(
    (notification: Omit<NotificationProps, 'onClose'>) => {
      const id = createSecureId();

      const newNotification: NotificationState = {
        ...notification,
        id
      };

      setNotifications(prev => [...prev, newNotification]);
      return id;
    },
    []
  );

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== id)
    );
  }, []);

  const showError = useCallback(
    (title: string, message: string, options?: Partial<NotificationProps>) => {
      return showNotification({
        title,
        message,
        variant: 'error',
        ...options
      });
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (title: string, message: string, options?: Partial<NotificationProps>) => {
      return showNotification({
        title,
        message,
        variant: 'notification',
        ...options
      });
    },
    [showNotification]
  );

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    showNotification,
    showError,
    showInfo,
    removeNotification,
    clearAll
  };
}
